import { Component, forwardRef, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subscription } from 'rxjs';
import { WalletDesignService } from '../../wallet-design.service';
import {
  WALLET_DESIGN_LAYOUTS,
  WALLET_DESIGN_ROTATIONS,
  WalletDesignAnalysis,
  WalletDesignLayout,
  WalletDesignPage,
  WalletDesignRotation,
  WalletDesignSettings
} from '../../wallet-design';
import { contrast, googleTextColor, loadImage, renderSquareLogo, renderStrip, rotate } from '../../wallet-design-renderer';

@Component({
  selector: 'app-wallet-design-wizard',
  templateUrl: './wallet-design-wizard.component.html',
  styleUrls: ['./wallet-design-wizard.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => WalletDesignWizardComponent),
    multi: true
  }]
})
export class WalletDesignWizardComponent implements ControlValueAccessor, OnChanges, OnDestroy {

  @Input()
  pdf: File;

  @Input()
  title: string;

  readonly rotations = WALLET_DESIGN_ROTATIONS;
  readonly layouts = WALLET_DESIGN_LAYOUTS;

  analysis: WalletDesignAnalysis;
  settings: WalletDesignSettings;
  enabled = true;
  loading = false;
  error: string;

  stripUrl: string;
  squareLogoUrl: string;

  private image: HTMLImageElement;
  private analysisSubscription: Subscription;
  private onChange: (value: WalletDesignSettings) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private walletDesignService: WalletDesignService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.pdf) {
      this.analyze();
    }
  }

  ngOnDestroy(): void {
    this.analysisSubscription?.unsubscribe();
  }

  get page(): WalletDesignPage {
    return this.analysis?.pages[this.settings?.page];
  }

  get portrait(): boolean {
    if (!this.page) {
      return false;
    }
    const quarter = this.settings.rotation === 90 || this.settings.rotation === 270;
    const width = quarter ? this.page.height : this.page.width;
    const height = quarter ? this.page.width : this.page.height;
    return width < height;
  }

  get contrastRatio(): number {
    return this.settings ? contrast(this.settings.background_color, this.settings.text_color) : 0;
  }

  get googleTextColor(): string {
    return this.settings ? googleTextColor(this.settings.background_color) : '#ffffff';
  }

  layoutDisabled(layout: WalletDesignLayout): boolean {
    return layout === 'artwork' && this.portrait;
  }

  async selectPage(index: number) {
    const page = this.analysis.pages[index];
    this.settings = {
      page: index,
      rotation: 0,
      layout: 'artwork',
      zoom: 1,
      background_color: page.background_color,
      text_color: page.text_color
    };
    this.image = await loadImage(page.image);
    this.selectRotation(0);
  }

  selectRotation(rotation: WalletDesignRotation) {
    this.settings = { ...this.settings, rotation };
    if (this.layoutDisabled(this.settings.layout)) {
      this.settings = { ...this.settings, layout: 'centered_logo' };
    }
    this.update();
  }

  selectLayout(layout: WalletDesignLayout) {
    if (this.layoutDisabled(layout)) {
      return;
    }
    this.settings = { ...this.settings, layout };
    this.update();
  }

  setZoom(zoom: string) {
    this.settings = { ...this.settings, zoom: Number(zoom) };
    this.update();
  }

  setColor(key: 'background_color' | 'text_color', value: string) {
    this.settings = { ...this.settings, [key]: value };
    this.update();
  }

  resetColors() {
    this.setColor('background_color', this.page.background_color);
    this.setColor('text_color', this.page.text_color);
  }

  toggleEnabled(enabled: boolean) {
    this.enabled = enabled;
    this.emit();
  }

  writeValue(value: WalletDesignSettings): void {
    if (value && this.analysis) {
      this.settings = value;
      this.update();
    }
  }

  registerOnChange(fn: (value: WalletDesignSettings) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  private analyze() {
    this.analysisSubscription?.unsubscribe();
    this.analysis = null;
    this.settings = null;
    this.stripUrl = null;
    this.squareLogoUrl = null;
    this.error = null;
    this.emit();
    if (!this.pdf) {
      return;
    }
    this.loading = true;
    this.analysisSubscription = this.walletDesignService.analyze(this.pdf).subscribe(async analysis => {
      this.analysis = analysis;
      this.loading = false;
      await this.selectPage(analysis.suggested_page);
    }, () => {
      this.loading = false;
      this.error = 'Die Druckvorlage konnte nicht analysiert werden.';
    });
  }

  private update() {
    if (!this.image || !this.page) {
      return;
    }
    const card = rotate(this.image, this.settings.rotation);
    const box = this.page.elements[this.settings.rotation];
    this.stripUrl = renderStrip(card, box, this.settings.layout, this.settings.zoom, this.settings.background_color);
    this.squareLogoUrl = renderSquareLogo(card, box, this.settings.background_color);
    this.emit();
    this.onTouched();
  }

  private emit() {
    this.onChange(this.enabled && this.settings ? { ...this.settings } : null);
  }
}
